/*
 * StackSchematic theme for Backstage.
 *
 * Built with Backstage's supported theming API (`createUnifiedTheme`,
 * `palettes`, `genPageTheme`) - see docs/upstream/backstage/INDEX.md. Nothing
 * in oss_apps/backstage is touched.
 *
 * The palette is deliberately "blueprint": deep navy grounds, a drafting-blue
 * primary and a cyan accent, so the product reads as a schematic of an estate
 * rather than as a stock Backstage install.
 */
import {
  createUnifiedTheme,
  genPageTheme,
  palettes,
  shapes,
} from '@backstage/theme';

const BLUEPRINT = {
  navy: '#0B2545',
  navyDeep: '#081B33',
  drafting: '#1D6FE0',
  cyan: '#22B8CF',
  slate: '#3E5C76',
  paper: '#FFFFFF',
  canvas: '#F4F7FB',
  amber: '#D98A2B',
  crimson: '#C2374B',
  green: '#2E8B62',
} as const;

/** Header gradients, keyed by the page types Backstage themes. */
const pageThemes = {
  home: genPageTheme({ colors: [BLUEPRINT.navy, BLUEPRINT.drafting], shape: shapes.wave }),
  documentation: genPageTheme({
    colors: [BLUEPRINT.navyDeep, BLUEPRINT.slate],
    shape: shapes.wave2,
  }),
  service: genPageTheme({ colors: [BLUEPRINT.navy, BLUEPRINT.cyan], shape: shapes.wave }),
  website: genPageTheme({ colors: [BLUEPRINT.navy, BLUEPRINT.cyan], shape: shapes.wave }),
  library: genPageTheme({ colors: [BLUEPRINT.slate, BLUEPRINT.cyan], shape: shapes.wave }),
  other: genPageTheme({ colors: [BLUEPRINT.navy, BLUEPRINT.slate], shape: shapes.wave }),
  app: genPageTheme({ colors: [BLUEPRINT.navy, BLUEPRINT.drafting], shape: shapes.wave }),
  apis: genPageTheme({ colors: [BLUEPRINT.navyDeep, BLUEPRINT.cyan], shape: shapes.wave2 }),
  tool: genPageTheme({ colors: [BLUEPRINT.slate, BLUEPRINT.drafting], shape: shapes.round }),
};

export const stackSchematicLight = createUnifiedTheme({
  fontFamily: '"Inter", "Helvetica Neue", Helvetica, Arial, sans-serif',
  palette: {
    ...palettes.light,
    primary: { main: BLUEPRINT.drafting, dark: BLUEPRINT.navy, light: BLUEPRINT.cyan },
    secondary: { main: BLUEPRINT.cyan, dark: BLUEPRINT.slate },
    navigation: {
      background: BLUEPRINT.navyDeep,
      indicator: BLUEPRINT.cyan,
      color: '#D6E4F0',
      selectedColor: '#FFFFFF',
      navItem: { hoverBackground: BLUEPRINT.navy },
    },
    background: { default: BLUEPRINT.canvas, paper: BLUEPRINT.paper },
    banner: {
      info: BLUEPRINT.drafting,
      error: BLUEPRINT.crimson,
      warning: BLUEPRINT.amber,
      text: '#FFFFFF',
      link: '#FFFFFF',
    },
    errorText: BLUEPRINT.crimson,
    warningText: BLUEPRINT.amber,
    infoText: BLUEPRINT.drafting,
    status: {
      ok: BLUEPRINT.green,
      warning: BLUEPRINT.amber,
      error: BLUEPRINT.crimson,
      pending: BLUEPRINT.slate,
      running: BLUEPRINT.drafting,
      aborted: '#8A94A6',
    },
  },
  defaultPageTheme: 'home',
  pageTheme: pageThemes,
});

export const stackSchematicDark = createUnifiedTheme({
  fontFamily: '"Inter", "Helvetica Neue", Helvetica, Arial, sans-serif',
  palette: {
    ...palettes.dark,
    primary: { main: BLUEPRINT.cyan, dark: BLUEPRINT.drafting, light: '#7FD9E8' },
    secondary: { main: BLUEPRINT.drafting },
    navigation: {
      background: '#050F1D',
      indicator: BLUEPRINT.cyan,
      color: '#9FB3C8',
      selectedColor: '#FFFFFF',
      navItem: { hoverBackground: BLUEPRINT.navyDeep },
    },
    background: { default: '#0A1626', paper: '#111F33' },
    banner: {
      info: BLUEPRINT.drafting,
      error: BLUEPRINT.crimson,
      warning: BLUEPRINT.amber,
      text: '#FFFFFF',
      link: '#FFFFFF',
    },
    status: {
      ok: BLUEPRINT.green,
      warning: BLUEPRINT.amber,
      error: BLUEPRINT.crimson,
      pending: BLUEPRINT.slate,
      running: BLUEPRINT.cyan,
      aborted: '#5A6675',
    },
  },
  defaultPageTheme: 'home',
  pageTheme: pageThemes,
});
